import { Alert, Button, Divider, MenuItem, TextField } from "@mui/material";
import SelectMachineField from "./FieldOperationsSubForms/SelectMachineField";
import OperationDetailsFields from "./FieldOperationsSubForms/OperationDetailsFields";
import { useEffect } from "react";
import HolosHarvestInputs from "./HolosHarvestInputs";

function numberOutside(value, minimum, maximum = Number.POSITIVE_INFINITY) {
  if (value === "" || value === null || value === undefined) return false;
  const parsed = Number(value);
  return !Number.isFinite(parsed) || parsed < minimum || parsed > maximum;
}

export default function HarvestForm({
  LowerPanel,
  panelControls,
  cropYear,
  setCropYear,
  farm,
  setFarm,
}) {
  const errorFields = {
    swather_machine_id: false,
    swather_hours_used: numberOutside(
      cropYear.harvest?.swather?.hours_used,
      0
    ),
    combine_machine_id: false,
    combine_hours_used: numberOutside(
      cropYear.harvest?.combine?.hours_used,
      0
    ),
    combine_avg_speed: numberOutside(
      cropYear.harvest?.combine?.avg_speed,
      0
    ),
    swather_fuel: numberOutside(
      cropYear.harvest?.swather?.fuel_used_litres,
      0
    ),
    combine_fuel: numberOutside(
      cropYear.harvest?.combine?.fuel_used_litres,
      0
    ),
    moisture_before: numberOutside(
      cropYear.harvest?.moisture?.before_drying,
      0,
      100
    ),
    moisture_after: numberOutside(
      cropYear.harvest?.moisture?.after_drying,
      0,
      100
    ),
  };
  const additionalCombines = cropYear.harvest?.additional_combines || [];
  const numericInputsAreValid = !Object.values(errorFields).some(Boolean) &&
    !additionalCombines.some((combine) => [combine.hours_used, combine.fuel_used_litres, combine.avg_speed].some((value) => numberOutside(value, 0))) &&
    !Object.entries(cropYear.holos_model_inputs?.field_parameters || {}).some(([key, value]) =>
      key.includes("Percentage") && numberOutside(value, 0, 100));

  function updateHarvestMachine(machineType, nextValues) {
    setCropYear((prevState) => ({
      ...prevState,
      harvest: {
        ...(prevState.harvest || {}),
        [machineType]: {
          ...(prevState.harvest?.[machineType] || {}),
          ...nextValues,
        },
      },
    }));
  }

  function handleMachineChange(machineId, machineType) {
    updateHarvestMachine(machineType, { machine_id: machineId });
  }

  function handleHoursChange(hoursUsed, machineType) {
    updateHarvestMachine(machineType, { hours_used: hoursUsed });
  }

  function handleSpeedChange(avgSpeed) {
    updateHarvestMachine("combine", { avg_speed: avgSpeed });
  }

  function updateHarvestField(field, value) {
    setCropYear((prevState) => ({
      ...prevState,
      harvest: { ...(prevState.harvest || {}), [field]: value },
    }));
  }

  function updateAdditionalCombine(index, changes) {
    setCropYear((current) => ({ ...current, harvest: { ...current.harvest,
      additional_combines: (current.harvest.additional_combines || []).map((combine, row) => row === index ? { ...combine, ...changes } : combine),
    } }));
  }

  function updateMoisture(field, value) {
    setCropYear((prevState) => ({
      ...prevState,
      harvest: {
        ...(prevState.harvest || {}),
        moisture: { ...(prevState.harvest?.moisture || {}), [field]: value },
      },
    }));
  }

  useEffect(() => {
    const element = document.getElementById("scrollableDiv");
    element.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="h-full w-full">
      <h3 className="text-[24px] font-semibold text-slate-800">Harvest</h3>
      <Divider sx={{ marginBottom: 2, marginTop: 1 }} />
      {!numericInputsAreValid ? (
        <Alert severity="error" className="mb-4 max-w-[760px]">
          Hours, speed, and fuel cannot be negative. Moisture must be from 0 to
          100%.
        </Alert>
      ) : null}

      <section>
        <h4 className="mb-3 text-[20px] font-semibold text-slate-700">
          Equipment
        </h4>
        <p className="mb-4 text-sm text-slate-600">
          Harvest equipment, hours and recorded fuel are optional. Recorded litres
          and hours are not converted to Holos energy. You can set diesel energy
          (GJ/ha) for all operations, including harvest, in Review; without an
          override, this input is zero. Yield moisture and residue inputs below
          can affect the calculation.
        </p>

        <div className="flex flex-wrap items-start gap-3">
          <div className="w-full max-w-[520px]">
            <SelectMachineField
              errorFound={{
                machineObj: errorFields.swather_machine_id,
                hoursUsed: errorFields.swather_hours_used,
              }}
              fieldLabel={"Select Swather"}
              machineType={"SWATHER"}
              farm={farm}
              setFarm={setFarm}
              handleMachineChange={(machineId) =>
                handleMachineChange(machineId, "swather")
              }
              handleHoursChange={(hoursUsed) =>
                handleHoursChange(hoursUsed, "swather")
              }
              fieldState={cropYear.harvest.swather}
            />
            <OperationDetailsFields
              cropYear={cropYear.crop_year}
              operation={cropYear.harvest.swather}
              error={errorFields.swather_fuel}
              dateLabel="Swathing Date"
              onChange={(field, value) =>
                updateHarvestMachine("swather", { [field]: value })
              }
            />
          </div>
        </div>

        <div className="mb-4 flex flex-wrap items-start gap-3">
          <div className="w-full max-w-[520px]">
            <SelectMachineField
              errorFound={{
                machineObj: errorFields.combine_machine_id,
                hoursUsed: errorFields.combine_hours_used,
              }}
              fieldLabel={"Select Combine"}
              machineType={"COMBINE"}
              farm={farm}
              setFarm={setFarm}
              handleMachineChange={(machineId) =>
                handleMachineChange(machineId, "combine")
              }
              handleHoursChange={(hoursUsed) =>
                handleHoursChange(hoursUsed, "combine")
              }
              fieldState={cropYear.harvest.combine}
            />
            <OperationDetailsFields
              cropYear={cropYear.crop_year}
              operation={cropYear.harvest.combine}
              error={errorFields.combine_fuel}
              dateLabel="Harvest Date"
              onChange={(field, value) =>
                updateHarvestMachine("combine", { [field]: value })
              }
            />
          </div>
          <TextField
            error={errorFields.combine_avg_speed}
            helperText={
              errorFields.combine_avg_speed ? "Use zero or a positive speed." : ""
            }
            type="number"
            label="Avg Speed Miles/Hr"
            size="small"
            sx={{ width: 200, marginTop: "12px" }}
            InputProps={{ inputProps: { min: 0 } }}
            onChange={(e) => handleSpeedChange(e.target.value)}
            value={cropYear.harvest.combine.avg_speed}
          />
        </div>
      </section>

      {additionalCombines.map((combine, index) => <section key={index} aria-label={`Additional combine ${index + 1}`} className="mb-4 max-w-[760px] border-t border-slate-200 pt-3">
        <h4 className="text-lg font-semibold">Additional combine {index + 1}</h4>
        <SelectMachineField fieldLabel={`Select Additional Combine ${index + 1}`} machineType="COMBINE" farm={farm} setFarm={setFarm}
          fieldState={combine} errorFound={{ machineObj: false, hoursUsed: numberOutside(combine.hours_used, 0) }}
          handleMachineChange={(machine_id) => updateAdditionalCombine(index, { machine_id })}
          handleHoursChange={(hours_used) => updateAdditionalCombine(index, { hours_used })} />
        <OperationDetailsFields cropYear={cropYear.crop_year} operation={combine} dateLabel="Harvest Date"
          error={numberOutside(combine.fuel_used_litres, 0)} onChange={(field, value) => updateAdditionalCombine(index, { [field]: value })} />
        <TextField size="small" type="number" label="Average speed (mph)" value={combine.avg_speed ?? ""}
          inputProps={{ min: 0 }} error={numberOutside(combine.avg_speed, 0)} onChange={(event) => updateAdditionalCombine(index, { avg_speed: event.target.value })} />
        <Button onClick={() => updateHarvestField("additional_combines", additionalCombines.filter((_, row) => row !== index))}>Remove additional combine {index + 1}</Button>
      </section>)}
      <Button disabled={additionalCombines.length >= 20} onClick={() => updateHarvestField("additional_combines", [...additionalCombines, { machine_id: "", hours_used: "", operation_date: "", fuel_used_litres: "", avg_speed: "" }])}>Add another combine</Button>
      <Divider sx={{ marginBottom: 2, marginTop: 2 }} />
      <HolosHarvestInputs cropYear={cropYear} setCropYear={setCropYear} />
      <section className="max-w-[900px]">
        <h4 className="mb-3 text-[20px] font-semibold text-slate-700">
          Moisture and crop drying
        </h4>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <TextField
            error={errorFields.moisture_before}
            select
            size="small"
            label="Drying Method"
            value={cropYear.harvest.crop_drying_type || ""}
            onChange={(event) =>
              updateHarvestField("crop_drying_type", event.target.value)
            }
          >
            <MenuItem value="">Not dried</MenuItem>
            <MenuItem value="Natural Air">Natural air</MenuItem>
            <MenuItem value="Heated Air">Heated air</MenuItem>
          </TextField>
          {cropYear.harvest.crop_drying_type ? (
            <TextField
              select
              size="small"
              label="Drying Energy Source"
              value={cropYear.harvest.crop_drying_fuel || ""}
              onChange={(event) =>
                updateHarvestField("crop_drying_fuel", event.target.value)
              }
            >
              {["Electricity", "Natural gas", "Propane", "Diesel"].map((fuel) => (
                <MenuItem key={fuel} value={fuel}>{fuel}</MenuItem>
              ))}
            </TextField>
          ) : null}
          <TextField
            type="number"
            size="small"
            label="Moisture Before Drying (%)"
            value={cropYear.harvest.moisture?.before_drying ?? ""}
            onChange={(event) => updateMoisture("before_drying", event.target.value)}
            inputProps={{ min: 0, max: 100, step: "any" }}
            helperText={errorFields.moisture_before ? "Use 0 to 100%." : ""}
          />
          <TextField
            error={errorFields.moisture_after}
            type="number"
            size="small"
            label="Moisture After Drying (%)"
            value={cropYear.harvest.moisture?.after_drying ?? ""}
            onChange={(event) => updateMoisture("after_drying", event.target.value)}
            inputProps={{ min: 0, max: 100, step: "any" }}
            helperText={errorFields.moisture_after ? "Use 0 to 100%." : ""}
          />
        </div>
      </section>

      <LowerPanel
        activeStep={panelControls.activeStep}
        handleBack={panelControls.handleBack}
        handleNext={panelControls.handleNext}
        steps={panelControls.steps}
        handleComplete={panelControls.handleComplete}
        completedSteps={panelControls.completedSteps}
        totalSteps={panelControls.totalSteps}
        isInputValid={() => numericInputsAreValid}
      />
    </div>
  );
}
